import { AdminLayout } from '@/components/layouts'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Link, useForm } from '@inertiajs/react'
import {
  HelpCircle,
  MessageSquare,
  Megaphone,
  Save,
  X
} from 'lucide-react'

interface PostDetail {
  id: number
  title: string
  body: string
  post_type: 'question' | 'discussion' | 'announcement'
  category_id: number | null
  community_id: number
}

interface Community {
  id: number
  name: string
}

interface Category {
  id: number
  name: string
  community_id: number
}

interface EditProps {
  post: PostDetail
  communities: Community[]
  categories: Category[]
}

const POST_TYPE_CONFIG = {
  question: {
    icon: HelpCircle,
    label: 'Question',
    className: 'bg-blue-100 text-blue-800 border-blue-200'
  },
  discussion: {
    icon: MessageSquare,
    label: 'Discussion',
    className: 'bg-gray-100 text-gray-700 border-gray-200'
  },
  announcement: {
    icon: Megaphone,
    label: 'Annonce',
    className: 'bg-yellow-100 text-yellow-800 border-yellow-200'
  }
}

export default function Edit({ post, communities, categories }: EditProps) {
  const { data, setData, patch, processing, errors } = useForm({
    title: post.title,
    body: post.body,
    post_type: post.post_type,
    category_id: post.category_id || ''
  })

  // Filter categories by community
  const filteredCategories = categories.filter(c => c.community_id === post.community_id)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    patch(`/admin/posts/${post.id}`)
  }

  return (
    <AdminLayout
      title="Modifier la publication"
      breadcrumbs={[
        { label: 'Publications', href: '/admin/posts' },
        { label: post.title, href: `/admin/posts/${post.id}` },
        { label: 'Modifier' }
      ]}
    >
      <form onSubmit={handleSubmit}>
        <div className="max-w-3xl">
          <Card className="p-6">
            {/* Post type selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-text-dark mb-2">
                Type de publication
              </label>
              <div className="flex gap-3">
                {(['question', 'discussion', 'announcement'] as const).map((type) => {
                  const config = POST_TYPE_CONFIG[type]
                  const Icon = config.icon
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => setData('post_type', type)}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all ${
                        data.post_type === type
                          ? `${config.className} border-current`
                          : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <Icon size={18} />
                      {config.label}
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Title */}
            <div className="mb-6">
              <label htmlFor="title" className="block text-sm font-medium text-text-dark mb-2">
                Titre
              </label>
              <Input
                id="title"
                type="text"
                value={data.title}
                onChange={(e) => setData('title', e.target.value)}
                placeholder="Titre de la publication"
                required
                minLength={5}
                maxLength={200}
              />
              {errors.title && (
                <p className="text-sm text-red-600 mt-1">{errors.title}</p>
              )}
            </div>

            {/* Body */}
            <div className="mb-6">
              <label htmlFor="body" className="block text-sm font-medium text-text-dark mb-2">
                Contenu
              </label>
              <textarea
                id="body"
                value={data.body}
                onChange={(e) => setData('body', e.target.value)}
                placeholder="Contenu de la publication..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-tessan-primary focus:border-transparent resize-none"
                rows={10}
                required
                minLength={10}
              />
              {errors.body && (
                <p className="text-sm text-red-600 mt-1">{errors.body}</p>
              )}
            </div>

            {/* Category */}
            <div className="mb-6">
              <label htmlFor="category" className="block text-sm font-medium text-text-dark mb-2">
                Categorie (optionnel)
              </label>
              <Select
                id="category"
                value={data.category_id}
                onChange={(e) => setData('category_id', e.target.value)}
              >
                <option value="">Aucune categorie</option>
                {filteredCategories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Select>
            </div>

            {/* Actions */}
            <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
              <Button
                type="submit"
                disabled={processing}
                className="flex items-center gap-2"
              >
                <Save size={16} />
                {processing ? 'Enregistrement...' : 'Enregistrer'}
              </Button>
              <Link href={`/admin/posts/${post.id}`}>
                <Button type="button" variant="secondary" className="flex items-center gap-2">
                  <X size={16} />
                  Annuler
                </Button>
              </Link>
            </div>
          </Card>
        </div>
      </form>
    </AdminLayout>
  )
}
